import React from 'react';
import ReactMarkdown from 'react-markdown';
import { CircularProgress, Typography, Box } from '@mui/material';
import { MessageGroup, MessageBubble, LoadingIndicator } from './styledComponents';

const markdownComponents = {
  code: ({ inline, children }) => (
    <Box
      component="code"
      sx={{
        fontFamily: 'monospace',
        px: inline ? 0.5 : 1,
        py: inline ? 0 : 0.75,
        display: inline ? 'inline' : 'block',
        bgcolor: inline ? 'transparent' : '#f3f4f6',
        borderRadius: 1,
        whiteSpace: inline ? 'pre-wrap' : 'pre-wrap',
        fontSize: '0.9rem',
      }}
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Box
      component="table"
      sx={{
        borderCollapse: 'collapse',
        width: '100%',
        my: 2,
        '& th, & td': {
          border: '1px solid',
          borderColor: 'divider',
          px: 1,
          py: 0.75,
          textAlign: 'left',
          fontSize: '0.92rem',
        },
        '& th': {
          fontWeight: 600,
          bgcolor: '#f3f4f6',
        },
      }}
    >
      {children}
    </Box>
  ),
};

// Add CSS for blinking cursor animation
export const BlinkingCursorStyle = () => (
  <style>
    {`
      @keyframes blink {
        0%, 50% { opacity: 1; }
        51%, 100% { opacity: 0; }
      }
    `}
  </style>
);

export const MessageRenderer = ({ message, index, isStreaming, isLastMessage }) => {
  // For AI SDK Data Stream Protocol, use message.content which gets updated incrementally
  // Also support message.parts as fallback for compatibility
  const textContent = message.content || 
    message.parts
      ?.map((part) => {
        if (part.type === 'text') return part.text || '';
        return '';
      })
      .join('') || '';

  const owner = message.role === 'user' ? 'user' : 'assistant';
  const isStreamingMessage = isStreaming && message.role === 'assistant' && isLastMessage;

  const renderAssistantMessage = () => (
    <MessageBubble owner={owner} elevation={0}>
      <Box
        sx={{
          fontSize: '0.95rem',
          lineHeight: 1.6,
          color: 'text.primary',
          '& p': {
            margin: 0,
            marginBottom: 1.25,
          },
          '& p:last-child': {
            marginBottom: 0,
          },
          '& ul, & ol': {
            paddingLeft: 3,
            margin: '0 0 1.25rem',
          },
          '& li': {
            marginBottom: 0.6,
          },
          '& li:last-child': {
            marginBottom: 0,
          },
          '& h1, & h2, & h3, & h4': {
            marginTop: 0,
            marginBottom: 1,
            fontWeight: 600,
          },
          '& h1': { fontSize: '1.1rem' },
          '& h2': { fontSize: '1.05rem' },
          '& h3': { fontSize: '1rem' },
          '& strong': { fontWeight: 600 },
      '& em': { fontStyle: 'italic' },
    }}
  >
    <ReactMarkdown components={markdownComponents}>
      {textContent || ''}
    </ReactMarkdown>
        {isStreamingMessage && (
          <Typography
            variant="body2"
            sx={{ opacity: 0.6, animation: 'blink 1s infinite' }}
          >
            |
          </Typography>
        )}
      </Box>
    </MessageBubble>
  );

  const renderUserMessage = () => (
    <MessageBubble owner={owner} elevation={0}>
      <Typography sx={{ whiteSpace: 'pre-wrap' }}>{textContent}</Typography>
    </MessageBubble>
  );

  return (
    <MessageGroup key={message.id} owner={owner}>
      {textContent || !isStreamingMessage ? (
        owner === 'assistant' ? renderAssistantMessage() : renderUserMessage()
      ) : (
        <LoadingIndicator>
          <CircularProgress size={16} sx={{ color: '#627CFB' }} />
          <Typography variant="body2">Drafting a response...</Typography>
        </LoadingIndicator>
      )}
    </MessageGroup>
  );
};

export const ErrorMessage = ({ error }) => (
  <MessageGroup owner="assistant">
    <MessageBubble owner="assistant" elevation={0}>
      <Typography variant="body2" color="#FF9B9B">
        {error.message || 'Something went wrong. Please try again.'}
      </Typography>
    </MessageBubble>
  </MessageGroup>
);
